import { useState } from 'react'

import { BiddingBidMarket } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import { TabsComponent as Tabs } from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { PeriodFilter } from '~/pages/JepxSettlement/components/PeriodFilter'
import { PeriodSummary } from '~/pages/JepxSettlement/components/PeriodSummary'
import { SettlementStatePanel } from '~/pages/JepxSettlement/components/SettlementStatePanel'
import { SettlementTable } from '~/pages/JepxSettlement/components/SettlementTable'
import { useListJepxSettlements } from '~/pages/JepxSettlement/hooks/useListJepxSettlements'
import { usePersistedJepxSettlementFilters } from '~/pages/JepxSettlement/hooks/usePersistedJepxSettlementFilters'

/** 市場タブ。 間接送電権・定額制の清算書はスポットのタブに出る (backend が市場ごとに返す)。 */
type MarketTabKey = 'spot' | 'intraday'

const MARKET_BY_TAB: Record<MarketTabKey, BiddingBidMarket> = {
  spot: BiddingBidMarket.SPOT,
  intraday: BiddingBidMarket.INTRADAY,
}

/**
 * 清算画面 (`docs/specs/bidding/jepx-settlement.md` §1 清算書の一覧)。
 *
 * 組織の JEPX 清算書を、 スポット市場・時間前市場のタブごとに、 清算日の期間を絞って確認する
 * **閲覧専用**の画面。 タブを切り替えるとその市場の清算書を取得する。 期間 (開始日・終了日) は
 * 市場を切り替えても保ち、 画面を離れて戻った時も復元する (AC-SC-031。 市場タブは復元しない)。
 * 一覧の並び (清算日の新しい順) と期間の絞り込みは backend が行う。
 *
 * 一覧の代わりに出す状態 (AC-JST-003〜005) は、 取得の結果で次の順に決める:
 * 取得失敗 (画面内の案内。 全画面エラーにしない) → 会員 ID 未設定 (応答の
 * `jepx_member_registered = false`。 会員情報の RPC は別に呼ばない) → 0 件 → 一覧。
 */
export const JepxSettlementPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [marketTab, setMarketTab] = useState<MarketTabKey>('spot')
  // 期間 (開始日・終了日) は他画面への遷移・再訪をまたいで復元する (AC-SC-031 [P0])。
  // 市場タブはフィルタバーの外の別軸なので保持しない (再訪時は常にスポットから)。
  const { from, setFrom, to, setTo, clear } =
    usePersistedJepxSettlementFilters()

  const { data, isError, isPending } = useListJepxSettlements({
    market: MARKET_BY_TAB[marketTab],
    settlementDateFrom: from,
    settlementDateTo: to,
  })

  const isPeriodSet = from !== undefined || to !== undefined
  const settlements = data?.settlements ?? []
  // 指定中の期間と件数 (AC-JST-002) は 0 件の空状態でも出し、 会員 ID 未設定・取得失敗・
  // 読み込み中は出さない (8080 `bidding/settlement.html` の `.stl-summary`)。
  const showsSummary =
    !isPending && !isError && data?.jepxMemberRegistered === true

  const tabs: Array<{ key: MarketTabKey; label: string }> = [
    { key: 'spot', label: t('page:jepxSettlement.tab.spot') },
    { key: 'intraday', label: t('page:jepxSettlement.tab.intraday') },
  ]

  const renderBody = (): React.ReactElement => {
    if (isPending) {
      return <SettlementTable isLoading rows={[]} />
    }
    if (isError) {
      return (
        // 1 文目は title、 残りは description に渡す (Compact Alert。 UI 辞書
        // `components/alert-placement.md` §0・§4)。
        <CompactAlert
          description={t('page:jepxSettlement.error.fetchFailedBody')}
          role="alert"
          status="error"
          title={t('page:jepxSettlement.error.fetchFailedTitle')}
        />
      )
    }
    if (!data.jepxMemberRegistered) {
      return <SettlementStatePanel kind="memberUnregistered" />
    }
    if (settlements.length === 0) {
      return (
        <SettlementStatePanel kind={isPeriodSet ? 'emptyInPeriod' : 'empty'} />
      )
    }
    return <SettlementTable isLoading={false} rows={settlements} />
  }

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('common:nav.bidding') },
        { label: t('common:route.biddingSettlement'), bold: true },
      ]}
    >
      <PageBody>
        <Tabs
          items={tabs}
          onSelect={setMarketTab}
          selected={marketTab}
          tabSx={{ fontSize: tabTriggerFontSize.raw }}
        />
        <PeriodFilter
          from={from}
          onClear={clear}
          onFromChange={setFrom}
          onToChange={setTo}
          to={to}
        />
        {showsSummary && (
          <PeriodSummary count={settlements.length} from={from} to={to} />
        )}
        <PageScrollArea>{renderBody()}</PageScrollArea>
      </PageBody>
    </PageLayout>
  )
}
